import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { SEARCH_MODEL } from '../../../../../../../components/table/Model/TableModel';
import { SimpleSearch } from '../../../../../../../reduxToolkit/Search/SearchApi';
import { TABLES } from '../../../../../../../Apis/Tables';
import { VIEW_TYPE } from '../../../../../../../enums/ViewType';
import TableFunction from '../../../../../../../components/table/TableFunction';
import routes from '../../../../../../../Routes/routes';
import DropDownMenuProfile from "../../../../../../../components/DropDown/DropDownMenuProfile";
import { permissionObj } from "../../../../../../../Helpers/permission";
import DropDownMenuItem from "../../../../../../../components/DropDown/DropDownMenuItem";
import {
    DetailsIconsOptions,
    UpdateIconsOptions,
    DeleteIconsOptions
} from "../../../../../../../components/buttons/IconButtons";
import { DeleteItemsApi } from '../../../../../../../reduxToolkit/Commons/CommonsApi';
import { errorMsg } from '../../../../../../../Apis/functions';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @Description Logic hook for the user deduction policy expandable table.
 * Owns paginated search of users with deduction policies and row expansion state.
 *
 * @param {Object} props
 * @param {string} props.searchElement - Current search input value.
 * @param {string} [props.view] - Table view type.
 * @returns {Object} Table state and handlers.
 */
const UserDeductionPolicyApp = ({
    registerAction,
    subView,
    searchElement = '',
    view = VIEW_TYPE.LIST.name,
    searchResults,
    setSearchResults,
    selectForDelete,
    setSelectForDelete,
    setIsAllChecked,
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;
    const DEV = useSelector((s) => s?.CommonsSlice?.development);
    const { permission } = useSelector((s) => s.authenticatioauthennSlice);

    const canRead   = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_DEDUCTION_POLICY_USER_READ);
    const canCreate = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_DEDUCTION_POLICY_USER_CREATE);
    const canUpdate = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_DEDUCTION_POLICY_USER_UPDATE);
    const canDelete = DEV || permission?.includes(permissionObj?.WEB_PAYROLL_DEDUCTION_POLICY_USER_DELETE);

    const [modified, setModified] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const handleCreate = () => navigate(routes.deductionPolicyUser.create, { state });

    // Opens the delete confirmation modal for the current selection.
    const handleDelete = () => setShowDeleteModal(true);

    // Register create and bulk-delete actions so the parent tab toolbar buttons work.
    // Re-register on selection change so the delete handler sees the latest selection.
    useEffect(() => {
        registerAction?.(subView, { handleCreate, handleDelete });
    }, [selectForDelete]);

    const columns = [
        { field: 'fullName', label: 'name' },
    ];

    /**
     * Fetches a paginated list of users with user-specific deduction policies.
     *
     * @param {number} page - Zero-based page index.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model: TABLES.PAYROLL_DEDUCTION_POLICY_USER,
            key: searchElement,
            view,
        });
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success === true) setSearchResults(data);
        });
    };

    const {
        t,
        handleRowExpand,
        pagination,
        page,
        rowsPerPage,
        openMenu,
        handleMenuToggle,
        handleCheckboxChange,
        handleDeleteAll
    } = TableFunction({
        selectedList: selectForDelete,
        setSelectedList: setSelectForDelete,
        data: searchResults,
        labelRowsPerPage: 'users_per_page',
        fixedPagination: false,
        hasContainer: true,
        setIsAllChecked,
        searchElement,
        getData,
        modified,
        setModified,
    });

    // Selects a single row and opens the delete modal (row options menu).
    const handleOpenDelete = (row) => {
        setSelectForDelete([row.id]);
        setShowDeleteModal(true);
    };

    const handleSubmitDelete = () => {
        if (selectForDelete.length <= 0) return;
        dispatch(DeleteItemsApi({ tableName: TABLES.PAYROLL_DEDUCTION_POLICY_USER, body: selectForDelete }))
            .then(({ payload: { data: { success, code } } }) => {
                if (success) {
                    setShowDeleteModal(false);
                    setSelectForDelete([]);
                    setIsAllChecked(false);
                    setModified(true);
                } else errorMsg(code);
            });
    };

    const renderOptionsMenu = (row) => (
        <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle} menuId={`menu-${row.id}`}>
            { canUpdate ?
                <DropDownMenuItem
                    index={row.id}
                    handleClose={handleMenuToggle}
                    name={t('update')}
                    click={() => navigate(routes.deductionPolicyUser.update, { state: { ...state, updateRow: row } })}
                    icon={<UpdateIconsOptions />}
                />
                :
                <DropDownMenuItem
                    index={row.id}
                    handleClose={handleMenuToggle}
                    name={t('details')}
                    click={() => navigate(routes.deductionPolicyUser.details, { state: { ...state, updateRow: row } })}
                    icon={<DetailsIconsOptions />}
                />
            }

            {canDelete && (
                <DropDownMenuItem
                    index={row.id}
                    handleClose={handleMenuToggle}
                    name={t('delete')}
                    click={() => handleOpenDelete(row)}
                    icon={<DeleteIconsOptions/>}
                    fontColor='font-color-error-600'
                    iconColor={"icon-color-error-code-600"}
                />
            )}
        </DropDownMenuProfile>
    );

    return {
        t,
        canRead,
        canCreate,
        canDelete,
        columns,
        handleRowExpand,
        pagination,
        page,
        rowsPerPage,
        handleCreate,
        renderOptionsMenu,
        showDeleteModal,
        setShowDeleteModal,
        handleSubmitDelete,
        selectForDelete,
        handleCheckboxChange,
        handleDeleteAll
    };
};

export default UserDeductionPolicyApp;
